import { Alert, Card, Radio, Stack, Text, Textarea } from '@mantine/core';
import { CONSENT_TEXT_MAX, parseConsentText } from '@quizee/core';
import {
  consentChoiceOf,
  inheritedConsent,
  inheritedNewsletterConsent,
  type ConsentChoice,
  type Inherited,
  type Layer,
} from '../brand/inheritance';
import { ConsentSegments } from './WidgetPreview';

/**
 * The two consent notices a publisher writes, which are one control and two subjects.
 *
 * They look alike and they are not the same statement, which is exactly why they share a
 * component: three choices, an override, and the same rule about an empty string being a value.
 * What differs is what the words are for, and that is the one thing an editor has to be able to
 * tell apart, so every sentence on screen comes from the table below rather than from a shared
 * phrase with the subject swapped into it.
 *
 * - `answers` is the notice a reader is shown before their answer is recorded.
 * - `newsletter` is the marketing consent a reader gives by ticking a box before handing over an
 *   email address. Its empty case is louder than the other one's: with nothing to show a reader
 *   there is nothing that could honestly be recorded, so the signup step is left out of every
 *   published document rather than shown without a notice (D30).
 */
export type ConsentSubject = 'answers' | 'newsletter';

interface SubjectCopy {
  /** The question the control answers, used as the radio group's label. */
  readonly question: string;
  readonly where: string;
  readonly inherit: (from: string) => string;
  readonly own: (scope: 'org' | 'brand') => string;
  readonly ownDescription: (scope: 'org' | 'brand', here: string) => string;
  readonly none: string;
  readonly noneDescription: string;
  readonly noneNote: string;
  /** How the read view says this level has deliberately chosen to show nothing. */
  readonly noneRead: string;
  readonly noneReadWhy: (scope: 'org' | 'brand') => string;
  readonly textLabel: string;
  readonly emptyRead: string;
}

const COPY: Readonly<Record<ConsentSubject, SubjectCopy>> = {
  answers: {
    question: 'What readers are told',
    where: 'Shown beside the interaction, before an answer is recorded.',
    inherit: (from) => `Use the notice inherited from ${from}`,
    own: (scope) => (scope === 'brand' ? 'Write a notice for this brand' : 'Write a notice'),
    ownDescription: (scope, here) =>
      scope === 'brand'
        ? 'Used here instead of the publisher default, whatever that default becomes later.'
        : `The starting point for ${here}. A brand can still write its own.`,
    none: 'Show readers no notice at all',
    noneDescription:
      'A deliberate choice, kept even if the notice above changes. Only right when your page tells readers this somewhere else.',
    noneNote:
      'Readers see nothing here. Their answers are still recorded, so the notice has to be somewhere they can read it.',
    noneRead: 'Readers are shown no notice at all, set here.',
    noneReadWhy: (scope) =>
      `A decision made ${scope === 'brand' ? 'on this brand' : 'for every brand'} and kept whatever the notice below it becomes. Answers are still recorded, so the notice has to be somewhere readers can find it.`,
    textLabel: 'The notice',
    emptyRead: 'Nothing, which means readers are shown no notice.',
  },
  newsletter: {
    question: 'What readers agree to when they join the newsletter',
    where: 'Shown beside the tick box at the end of an interaction that offers the newsletter.',
    inherit: (from) => `Use the newsletter wording inherited from ${from}`,
    own: (scope) =>
      scope === 'brand'
        ? 'Write a newsletter wording for this brand'
        : 'Write a newsletter wording',
    ownDescription: (scope, here) =>
      scope === 'brand'
        ? 'Used here instead of the publisher default, whatever that default becomes later. An interaction can still write its own.'
        : `The starting point for ${here}. A brand, and a single interaction, can still write their own.`,
    none: 'Offer no newsletter signup at all',
    noneDescription:
      'A deliberate choice, kept even if the wording above changes. Nothing published can then ask readers for an email address.',
    noneNote:
      'Readers are never offered the newsletter. Asking somebody to tick a box means showing them what they are agreeing to, so with no wording the signup step is left out of everything you publish.',
    noneRead: 'Readers are never offered the newsletter, set here.',
    noneReadWhy: (scope) =>
      `A decision made ${scope === 'brand' ? 'on this brand' : 'for every brand'} and kept whatever the wording below it becomes. Asking somebody to tick a box means showing them what they are agreeing to, so with no wording the signup step is left out of everything you publish.`,
    textLabel: 'The newsletter wording',
    emptyRead: 'Nothing, which means no interaction can offer the newsletter.',
  },
};

/** Which layer answers this subject, walked in the words the settings screens use. */
function inheritedFor(
  subject: ConsentSubject,
  below: readonly Layer[],
  system: string,
): Inherited<string> {
  return subject === 'newsletter'
    ? inheritedNewsletterConsent(below, system)
    : inheritedConsent(below, system);
}

/**
 * What readers are actually told here, and who decided it.
 *
 * The notice is the one setting on these screens that a lawyer may end up reading, so the read
 * view shows the wording itself rather than the fact that a wording exists, and it shows it as
 * readers see it: a link renders as a link, because "click here" pointing at nothing is exactly
 * the mistake this is worth catching before it is published.
 *
 * Showing nothing and inheriting are written as different sentences, because they are different
 * decisions: one survives whatever the publisher does next, the other follows it.
 */
export function ConsentRead({
  value,
  below,
  system,
  scope,
  subject = 'answers',
}: {
  /** What this level has set, where null means it takes whatever the level below says. */
  value: string | null;
  below: readonly Layer[];
  system: string;
  scope: 'org' | 'brand';
  subject?: ConsentSubject;
}) {
  const copy = COPY[subject];
  const choice = consentChoiceOf(value);
  const inherited = inheritedFor(subject, below, system);
  const shown = choice === 'inherit' ? inherited.value : (value ?? '');
  const segments = parseConsentText(shown);

  if (choice === 'none') {
    return (
      <>
        <Text>{copy.noneRead}</Text>
        <Text size="sm" c="dimmed" mt={4}>
          {copy.noneReadWhy(scope)}
        </Text>
      </>
    );
  }

  return (
    <>
      <Text size="sm" c="dimmed">
        {copy.question}, {choice === 'own' ? 'set here' : `inherited from ${inherited.from}`}
      </Text>
      {segments.length === 0 ? (
        <Text mt={4}>{copy.emptyRead}</Text>
      ) : (
        <Card withBorder radius="md" p="sm" mt={4} bg="var(--mantine-color-body)">
          <Text size="sm">
            <ConsentSegments segments={segments} />
          </Text>
        </Card>
      )}
    </>
  );
}

/**
 * The line a reader is shown before their answer is recorded, or before they join a newsletter.
 *
 * Three choices rather than one box, because the two that look alike are not the same thing.
 * Inheriting means this level says nothing and takes whatever the level below says, so changing
 * the publisher's notice later changes this one too. Showing nothing is a decision made here, and
 * it survives whatever the publisher does afterwards. A single box that cleared to empty could not
 * tell them apart, and an editor who meant "go back to our usual wording" would have quietly turned
 * the notice off.
 */
export function ConsentSection({
  choice,
  text,
  onChoice,
  onText,
  below,
  system,
  canEdit,
  scope,
  idPrefix,
  subject = 'answers',
}: {
  choice: ConsentChoice;
  /** What is in the box, kept while the choice moves so nothing typed is lost. */
  text: string;
  onChoice: (choice: ConsentChoice) => void;
  onText: (text: string) => void;
  below: readonly Layer[];
  system: string;
  canEdit: boolean;
  scope: 'org' | 'brand';
  /**
   * What every input id starts with. Both subjects can be on one screen, so this has to tell them
   * apart as well as telling the org's screen from a brand's.
   */
  idPrefix: string;
  subject?: ConsentSubject;
}) {
  const copy = COPY[subject];
  const inherited = inheritedFor(subject, below, system);
  // Read back as words: what is stored is sanitised markup, and an editor should not have to look
  // at an anchor tag to find out what their own notice says.
  const inheritedWords = parseConsentText(inherited.value)
    .map((segment) => segment.text)
    .join('');
  const here = scope === 'brand' ? 'this brand' : 'every brand';

  return (
    <Stack gap="md">
      <Radio.Group
        value={choice}
        onChange={(next) => onChoice(next as ConsentChoice)}
        label={copy.question}
        description={copy.where}
      >
        <Stack gap="sm" mt="xs">
          <Radio
            id={`${idPrefix}-consent-inherit`}
            value="inherit"
            disabled={!canEdit}
            label={copy.inherit(inherited.from)}
            description={
              inheritedWords === ''
                ? subject === 'newsletter'
                  ? 'Which is currently nothing at all, so nothing can offer the newsletter.'
                  : 'Which is currently no notice at all.'
                : `Currently: ${inheritedWords}`
            }
          />
          <Radio
            id={`${idPrefix}-consent-own`}
            value="own"
            disabled={!canEdit}
            label={copy.own(scope)}
            description={copy.ownDescription(scope, here)}
          />
          <Radio
            id={`${idPrefix}-consent-none`}
            value="none"
            disabled={!canEdit}
            label={copy.none}
            description={copy.noneDescription}
          />
        </Stack>
      </Radio.Group>

      {choice === 'own' && (
        <>
          <Textarea
            id={`${idPrefix}-consent-text`}
            label={copy.textLabel}
            description="A link, bold and italic are allowed. Anything else is removed when you save, so the preview below is what readers get."
            autosize
            minRows={2}
            maxRows={6}
            maxLength={CONSENT_TEXT_MAX}
            disabled={!canEdit}
            value={text}
            onChange={(event) => onText(event.currentTarget.value)}
          />
          <ConsentPreview text={text} />
        </>
      )}

      {choice === 'none' && (
        <Text size="sm" c="dimmed">
          {copy.noneNote}
        </Text>
      )}
    </Stack>
  );
}

/**
 * What the server actually stored, when it is not what was sent.
 *
 * The text is sanitised on the way in and the stored form is the one readers get, so it is shown
 * rather than assumed. It belongs with the saved value rather than in the form, because by the
 * time there is anything to say the form has been sent and closed.
 */
export function SavedDifference({ text }: { text: string | null }) {
  if (text === null) return null;
  return (
    <Alert color="gray" variant="light" radius="md" mt="sm">
      <Text size="sm" fw={500}>
        We saved a tidied version of what you typed.
      </Text>
      <Text size="sm" mt={4}>
        Markup we do not allow is taken out on the way in, so this is the wording readers actually
        get: {text}
      </Text>
    </Alert>
  );
}

/**
 * How the notice will read.
 *
 * Parsed by the same function the server sanitises with, so what survives here is exactly what
 * survives the save: a link, bold, italic, and the text of everything else.
 */
export function ConsentPreview({ text }: { text: string }) {
  const segments = parseConsentText(text);

  return (
    <Card withBorder radius="md" p="sm" bg="var(--mantine-color-body)">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
        How it will read
      </Text>
      {segments.length === 0 ? (
        <Text size="sm" c="dimmed">
          Nothing yet.
        </Text>
      ) : (
        <Text size="sm">
          <ConsentSegments segments={segments} />
        </Text>
      )}
    </Card>
  );
}
